<template>
    <div>
        <!-- Selectors -->
        <div>
            <div class="custom-text-input">
                <label class="left-div">Draw Number</label>
                <div class="right-div draw-selector" :class="{ 'error-input': formError.drawNumber }">
                    <div class="draw-input-wrapper">
                        <input 
                            type="text" 
                            list="existing-draws"
                            v-model="localForm.drawNumber" 
                            placeholder="Select existing or enter new draw number"
                            @input="handleDrawInput"
                        />
                        <datalist id="existing-draws">
                            <option v-for="draw in existingDraws" :key="draw.id" :value="draw.draw_number">
                                {{ draw.draw_number }} ({{ formatDate(draw.period_through) }})
                            </option>
                        </datalist>
                    </div>
                    <small v-if="suggestedDrawNumber && !existingDraws.some(d => d.draw_number === localForm.drawNumber)" class="hint">
                        Suggested next draw: {{ suggestedDrawNumber }}
                    </small>
                    <div v-if="formError.drawNumber" class="error-message">Draw Number is Required</div>
                </div>
            </div>
            <div class="custom-text-input">
                <label class="left-div width-100">Date</label>
                <div class="right-div" :class="{ 'error-input': formError.periodThroughDate }">
                    <VueDatePicker v-model="localForm.periodThroughDate" :enable-time-picker="false"
                        format="yyyy-MM-dd"></VueDatePicker>
                    <div v-if="formError.periodThroughDate" class="error-message">Period Through is Required
                    </div>
                </div>
            </div>
        </div>
        <!-- Input File Container -->
        <div class="draw-drop-file-container">
            <!-- Borrower Requisition Letter -->
            <div>
                <slot name="borrowerRequisitionLetter">
                    <DropFile :header-title="'Borrower Requisition Letter'"
                        :header-sub-title="'Download Template (Optional)'" :name="'borrowerRequisitionLetter'"
                        v-on:uploaded-files="matchFileContainers($event, 'borrowerRequisitionLetter')" />
                </slot>
            </div>
            <!-- Developer Draw Tracker -->
            <div>
                <DropFile :header-title="'Developer Draw Tracker'" :header-sub-title="'Download Template (Optional)'"
                    :name="'developerDrawTracker'"
                    v-on:uploaded-files="matchFileContainers($event, 'developerDrawTracker')" />
            </div>
            <!-- Full Payment Application -->
            <div>
                <DropFile :header-title="'Full Payment Application'"
                    :header-sub-title="'Download Template (Optional)'" :name ="'fullPaymentApplication'"
                    v-on:uploaded-files=" matchFileContainers($event, 'fullPaymentApplication' )" />
            </div>
            <!-- Soft Cost Invoices-->
            <div>
                <DropFile :header-title=" 'Soft Cost Invoices' " :name=" 'softCostInvoices' "
                    v-on:uploaded-files="matchFileContainers($event, 'softCostInvoices')" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { defineExpose, defineProps, reactive, ref, onMounted, inject } from 'vue';
import DropFile from "@/shared/DropFile.vue";
import axios from 'axios';

const props = defineProps({
    form: {
        type: Object, default: () => ({
            drawNumber: null,
            periodThroughDate: null,
            borrowerRequisitionLetter: { values: [] },
            developerDrawTracker: { values: [] },
            fullPaymentApplication: { values: [] },
            softCostInvoices: { values: [] }
        })
    },
    projectId: {
        type: [String, Number],
        default: null
    }
})

const formError = reactive({
    drawNumber: false,
    periodThroughDate: false,
});

let localForm = reactive({ ...props.form });

const emit = defineEmits(['update:files']);

// New refs for draw functionality
const existingDraws = ref([]);
const suggestedDrawNumber = ref('');
const authToken = inject('authToken', {});
const apiBase = process.env.VUE_APP_ENV_API_BASE || "http://localhost:3001/api/v1/";

// Fetch existing draws when component mounts or project changes
onMounted(() => {
    if (props.projectId) {
        fetchProjectDraws();
    }
});

async function fetchProjectDraws() {
    try {
        const response = await axios.get(
            `${apiBase}draws/for_project/${props.projectId}`,
            authToken
        );
        
        if (response.data) {
            existingDraws.value = response.data.draws || [];
            suggestedDrawNumber.value = response.data.next_suggested_draw || '1';
            
            // If no draws exist, pre-fill with suggested draw
            if (existingDraws.value.length === 0 && !localForm.drawNumber) {
                localForm.drawNumber = suggestedDrawNumber.value;
            }
        }
    } catch (error) {
        console.error('Error fetching project draws:', error);
        // Default to suggesting "1" if fetch fails
        suggestedDrawNumber.value = '1';
    }
}

function handleDrawInput(event) {
    // Validation will happen on submit
    // This allows users to enter any draw number they want
}

function formatDate(dateString) {
    if (!dateString) return 'No date';
    const date = new Date(dateString);
    return date.toLocaleDateString();
}

const matchFileContainers = (event, containerName) => {
    console.log(`Received files for ${containerName}:`, event);
    
    switch (containerName) {
        case 'borrowerRequisitionLetter':
            localForm.borrowerRequisitionLetter.values = event;
            console.log('Updated borrowerRequisitionLetter.values:', localForm.borrowerRequisitionLetter.values);
            break;
        case 'developerDrawTracker':
            localForm.developerDrawTracker.values = event;
            console.log('Updated developerDrawTracker.values:', localForm.developerDrawTracker.values);
            break;
        case 'fullPaymentApplication':
            localForm.fullPaymentApplication.values = event;
            console.log('Updated fullPaymentApplication.values:', localForm.fullPaymentApplication.values);
            break;
        case 'softCostInvoices':
            localForm.softCostInvoices.values = event;
            console.log('Updated softCostInvoices.values:', localForm.softCostInvoices.values);
            break;
        default:
            break;
    }
    
    // Check if any container has files and emit the update
    const allFiles = [];
    
    // Safely add files from each container if they exist
    if (localForm.borrowerRequisitionLetter && Array.isArray(localForm.borrowerRequisitionLetter.values)) {
        allFiles.push(...localForm.borrowerRequisitionLetter.values);
    }
    
    if (localForm.developerDrawTracker && Array.isArray(localForm.developerDrawTracker.values)) {
        allFiles.push(...localForm.developerDrawTracker.values);
    }
    
    if (localForm.fullPaymentApplication && Array.isArray(localForm.fullPaymentApplication.values)) {
        allFiles.push(...localForm.fullPaymentApplication.values);
    }
    
    if (localForm.softCostInvoices && Array.isArray(localForm.softCostInvoices.values)) {
        allFiles.push(...localForm.softCostInvoices.values);
    }
    
    emit('update:files', allFiles);
}

defineExpose({
    localForm
})
</script>

<style scoped lang="scss">
@import "./src/assets/main.scss";

/* Style for the input drop files */
.draw-drop-file-container {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-gap: 10px;
    margin-top: 20px;
    overflow-y: scroll;
    height: 100%;

    div {
        padding-bottom: 10px;
    }
}

.draw-drop-file-container::-webkit-scrollbar {
    width: 5px;
    height: 8px;
    background-color: $white1;
}

.draw-drop-file-container::-webkit-scrollbar-thumb {
    background: $lightGray1;
}

// Input CSS
.custom-text-input {
    display: flex;
    align-items: center;
    font-size: 12px;
    padding: 5px 0px;

    label {
        font-weight: 100;
    }

    .left-div {
        width: 10%;
    }

    .right-div {
        width: 20%;

        &.draw-selector {
            width: 30%;
            
            .draw-input-wrapper {
                width: 100%;
                
                input[type="text"] {
                    width: 100%;
                    color: $brightBlue1;
                    background-color: $white1;
                    border: 1px solid $lightGray1;
                    border-radius: 5px;
                    padding: 10px;
                    height: 35px;
                    box-sizing: border-box;
                }

                input[type="text"]:focus {
                    border-color: $brightBlue1;
                    outline: none;
                }
            }
            
            .hint {
                display: block;
                color: #666;
                font-size: 11px;
                margin-top: 4px;
                font-style: italic;
            }
        }
    }

    input[type=number] {
        color: $brightBlue1;
        background-color: $white1;
        border: 1px solid $lightGray1;
        border-radius: 5px;
        padding: 10px;
        height: 10px;
        width: 25%;
    }

    input[type=number]:focus {
        border-color: $brightBlue1;
        outline: $brightBlue1;
    }
}

/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

/* Firefox */
input[type=number] {
    -moz-appearance: textfield;
    text-align: right;
}

.error-message {
    color: $red3;
    font-size: 10px;
    padding-top: 5px;
}

.error-input {
    input[type=number] {
        border: 1px solid $red3;
    }

    &.input[type=number]:focus {
        border-color: $red3;
        outline: $red3;
    }

    .dp__theme_light {
        --dp-border-color: #ea5a4f;
        --dp-border-color-hover: #ea5a4f;
        --dp-border-color-focus: #ea5a4f;
    }
}
</style>